/**
 * The Team page (docs/plans/teams.md T7): what each member is doing now and how their week is going,
 * from docs every phone of an unrestricted member already has — clock sessions (stamped ownerUid by the
 * server), tasks (assignee) and the published team list. Pure — no I/O.
 */
import { weekKey, weekDays } from './weekplan.js';
import { inReview } from './commands/tasks.js';
import { currentMembers, leftoverTasks, pastMembers } from './people.js';

export * from './people.js';
export * from './jobpay.js';    // pay by job type (v0.1.258)
export * from './sharepay.js';   // people.md P7 — share pay on fixed jobs
export * from './hire.js';       // people.md P6 — the hire wizard

const STALE_MS = 16 * 3600000;   // an open session older than this is a phone that never clocked out, not a person at work

/**
 * Who is on the clock: the member's open session (no endedAt), newest first.
 * → { projectId, startedAt, travel, stale } | null
 */
export function memberNow(sessions = [], uid, now = Date.now()) {
  const open = sessions
    .filter((s) => s && s.ownerUid === uid && !s.endedAt && Number.isFinite(s.startedAt) && s.startedAt <= now)
    .sort((a, b) => b.startedAt - a.startedAt);
  if (!open.length) return null;
  const work = open.find((s) => s.type !== 'travel') || open[0];
  return { projectId: work.projectId || null, startedAt: work.startedAt, travel: open.some((s) => s.type === 'travel'), stale: now - work.startedAt > STALE_MS };
}

/**
 * A member's week (Mon–Sun of `date`): clocked time (work sessions; an open one counts to now, a stale
 * one doesn't), tasks assigned to them done this week, and the ones still open.
 */
export function memberWeek({ sessions = [], tasks = [] }, uid, date, now = Date.now()) {
  const days = weekDays(weekKey(date));
  const inWeek = (d) => d >= days[0] && d <= days[6];
  const start = new Date(`${days[0]}T00:00:00`).getTime();
  const end = new Date(`${days[6]}T00:00:00`).getTime() + 86400000;
  let clockedMs = 0;
  for (const s of sessions) {
    if (!s || s.ownerUid !== uid || s.type === 'travel' || !inWeek(s.date || '')) continue;
    if (s.endedAt) clockedMs += Math.max(0, Number(s.duration) || s.endedAt - s.startedAt || 0);
    else if (Number.isFinite(s.startedAt) && now - s.startedAt <= STALE_MS) clockedMs += Math.max(0, now - s.startedAt);
  }
  const mine = tasks.filter((t) => t && t.assignee === uid && !t.bucket);
  const done = mine.filter((t) => t.status === 'done' && t.completedAt >= start && t.completedAt < end);
  const open = mine.filter((t) => t.status !== 'done');
  return { days, clockedMs, done, open };
}

/** The team in list order: people on the clock first, then by name; assistants (Willow) last. */
export function teamOrder(members = [], sessions = [], now = Date.now()) {
  return currentMembers(members)
    .map((m) => ({ ...m, uid: m.uid || m.id, now: memberNow(sessions, m.uid || m.id, now) }))
    .sort((a, b) => (!!a.assistantFor - !!b.assistantFor)
      || (!!b.now && !b.now.stale) - (!!a.now && !a.now.stale)
      || String(a.name || '').localeCompare(String(b.name || '')));
}

/** This member's own clock sessions (a session without ownerUid predates the server stamp — it's theirs). */
export function ownSessions(sessions = [], uid) {
  return uid ? sessions.filter((s) => s && (!s.ownerUid || s.ownerUid === uid)) : sessions;
}

// ---- the dashboard + a member's analytics ----------------------------------------------------

const dayStartMs = (ymd) => new Date(`${ymd}T00:00:00`).getTime();

/** One work session's clocked time inside [from, to): open ones count to `now` unless stale. */
function clipped(s, from, to, now) {
  const start = Number(s.startedAt);
  if (!Number.isFinite(start)) return 0;
  const end = s.endedAt ? Number(s.endedAt) : (now - start <= STALE_MS ? now : start);
  return Math.max(0, Math.min(end, to) - Math.max(start, from));
}

/** A member's clocked time per day of the week (Mon..Sun), by project too. */
export function memberDays(sessions = [], uid, date, now = Date.now()) {
  const days = weekDays(weekKey(date));
  const perDay = days.map(() => 0);
  const byProject = new Map();
  for (const s of sessions) {
    if (!s || s.ownerUid !== uid || s.type === 'travel') continue;
    days.forEach((d, i) => {
      const ms = clipped(s, dayStartMs(d), dayStartMs(d) + 86400000, now);
      if (!ms) return;
      perDay[i] += ms;
      const k = s.projectId || null;
      byProject.set(k, (byProject.get(k) || 0) + ms);
    });
  }
  return {
    days, perDay, totalMs: perDay.reduce((a, b) => a + b, 0),
    projects: [...byProject].map(([projectId, ms]) => ({ projectId, ms })).sort((a, b) => b.ms - a.ms),
  };
}

/** Did this member finish this task? Assigned to them, or (unassigned) the last to touch it. */
const doneBy = (t, uid) => t.status === 'done' && (t.assignee ? t.assignee === uid : t.updatedBy === uid);

/** The whole team's week: who's on the clock now, hours, tasks done, work still assigned. */
export function teamWeek({ members = [], sessions = [], tasks = [] }, date, now = Date.now()) {
  const days = weekDays(weekKey(date));
  const from = dayStartMs(days[0]); const to = dayStartMs(days[6]) + 86400000;
  const rows = currentMembers(members).map((m) => {
    const uid = m.uid || m.id;
    const d = memberDays(sessions, uid, date, now);
    const done = tasks.filter((t) => doneBy(t, uid) && t.completedAt >= from && t.completedAt < to).length;
    const open = tasks.filter((t) => t.assignee === uid && t.status !== 'done' && !t.bucket).length;
    return { uid, member: m, now: memberNow(sessions, uid, now), perDay: d.perDay, totalMs: d.totalMs, done, open };
  });
  return {
    days, rows,
    onClock: rows.filter((r) => r.now && !r.now.stale).length,
    totalMs: rows.reduce((a, r) => a + r.totalMs, 0),
    done: rows.reduce((a, r) => a + r.done, 0),
    open: rows.reduce((a, r) => a + r.open, 0),
  };
}

/**
 * What a member did, newest first: clocked in / out (and drives), tasks finished, comments, notes.
 * → [{ at, kind: 'in' | 'out' | 'drive' | 'done' | 'comment' | 'note', projectId?, taskId?, text?, ms? }]
 */
export function memberActivity({ sessions = [], tasks = [], comments = [], notes = [] }, uid, { from = 0, to = Infinity, limit = 200 } = {}) {
  const out = [];
  const inside = (at) => Number.isFinite(at) && at >= from && at < to;
  for (const s of sessions) {
    if (!s || s.ownerUid !== uid) continue;
    if (s.type === 'travel') { if (inside(s.startedAt)) out.push({ at: s.startedAt, kind: 'drive', projectId: s.projectId || null, ms: s.endedAt ? s.endedAt - s.startedAt : null }); continue; }
    if (inside(s.startedAt)) out.push({ at: s.startedAt, kind: 'in', projectId: s.projectId || null });
    if (s.endedAt && inside(s.endedAt)) out.push({ at: s.endedAt, kind: 'out', projectId: s.projectId || null, ms: s.endedAt - s.startedAt });
  }
  for (const t of tasks) if (doneBy(t, uid) && inside(t.completedAt)) out.push({ at: t.completedAt, kind: 'done', taskId: t.id, projectId: t.projectId || null, text: t.title });
  for (const c of comments) if (c?.by?.uid === uid && inside(c.at)) out.push({ at: c.at, kind: 'comment', taskId: c.taskId || null, projectId: c.projectId || null, text: c.text });
  for (const n of notes) if (n && n.ownerUid === uid && inside(n.createdAt)) out.push({ at: n.createdAt, kind: 'note', projectId: n.projectId || null, text: n.title || '' });
  // Same minute: what happened later reads first (clocked out, then drove off).
  const RANK = { comment: 0, note: 0, done: 1, in: 2, drive: 3, out: 4 };
  return out.sort((a, b) => b.at - a.at || RANK[a.kind] - RANK[b.kind]).slice(0, limit);
}

// ---- the dashboard (docs/plans/team-dashboard.md) ------------------------------------------------


const H = 3600000;
const localYmd = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/** A job's labour against its quote, from the books' published numbers (opsJobs). null = not a costed job. */
export function jobLabour(job) {
  if (!job) return null;
  const budget = Number(job.budget?.labor ?? job.budget?.labour ?? 0) || 0;
  const posted = Number(job.actual?.labor ?? job.actual?.labour ?? 0) || 0;
  const used = posted + (Number(job.labourEstimate) || 0);
  return {
    budget, used, posted, unposted: Number(job.labourEstimate) || 0, unpostedHours: Number(job.unpostedHours) || 0,
    pct: budget > 0 ? used / budget : null, over: budget > 0 && used > budget,
    contract: Number(job.contractValue) || 0, marginPct: Number.isFinite(job.projectedMarginPct) ? job.projectedMarginPct : null,
  };
}

/**
 * Everything the Team dashboard shows, from docs already on an unrestricted member's phone.
 * input: { members, sessions (everyone's), tasks, projects, jobs, teamStats (opsTeamStats/time), comments, notes }
 * → { needs, now, today, jobs, feed, assistants, week }
 */
export function teamDashboard(input, { now = Date.now(), date = localYmd(now), me = null } = {}) {
  const { members = [], sessions = [], tasks = [], projects = [], jobs = [], teamStats = null, comments = [], notes = [] } = input;
  const project = (id) => projects.find((p) => p.id === id) || null;
  const pname = (id) => project(id)?.name || null;
  const tname = (id) => tasks.find((t) => t.id === id)?.title || null;
  const people = currentMembers(members).map((m) => ({ ...m, uid: m.uid || m.id })).filter((m) => m.uid);   // people.md P4: past people are listed apart (`past`)
  const crew = people.filter((m) => !m.assistantFor);
  const dayStart = new Date(`${date}T00:00:00`).getTime();
  const dayEnd = dayStart + 86400000;
  const hour = new Date(now).getHours();

  // Now: each person's open work session, the task on it, a break, a drive.
  const nowRows = crew.map((m) => {
    const open = sessions.filter((s) => s.ownerUid === m.uid && !s.endedAt && Number.isFinite(s.startedAt) && s.startedAt <= now).sort((a, b) => b.startedAt - a.startedAt);
    const work = open.find((s) => s.type !== 'travel') || null;
    const drive = open.find((s) => s.type === 'travel') || null;
    const s = work || drive;
    return {
      uid: m.uid, member: m,
      state: !s ? 'off' : (now - s.startedAt > STALE_MS ? 'stale' : drive ? 'driving' : work?.currentPause ? 'break' : 'on'),
      projectId: s?.projectId || null, project: pname(s?.projectId), since: s?.startedAt || null,
      taskId: work?.currentTaskId || null, task: tname(work?.currentTaskId), taskSince: work?.currentTaskAt || null, pause: work?.currentPause || null,
    };
  }).sort((a, b) => ({ on: 0, driving: 1, break: 2, stale: 3, off: 4 }[a.state] - { on: 0, driving: 1, break: 2, stale: 3, off: 4 }[b.state]) || String(a.member.name).localeCompare(String(b.member.name)));

  // Today: planned (assigned, dated today or finished today) vs done, hours so far, what's next.
  const todayRows = crew.map((m) => {
    const mine = tasks.filter((t) => t.assignee === m.uid && !t.bucket);
    const planned = mine.filter((t) => t.scheduledDate === date || (t.status === 'done' && t.completedAt >= dayStart && t.completedAt < dayEnd));
    const done = planned.filter((t) => t.status === 'done');
    const overdue = mine.filter((t) => t.status !== 'done' && t.scheduledDate && t.scheduledDate < date);
    // Next up: the first open task planned for today (their order); none today → the oldest overdue one.
    const openNow = mine.filter((t) => t.status !== 'done' && !inReview(t));
    const next = openNow.filter((t) => t.scheduledDate === date).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))[0]
      || openNow.filter((t) => t.scheduledDate && t.scheduledDate < date).sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate))[0] || null;
    const ms = memberDays(sessions, m.uid, date, now).perDay[new Date(`${date}T12:00:00`).getDay() === 0 ? 6 : new Date(`${date}T12:00:00`).getDay() - 1];
    return { uid: m.uid, member: m, planned: planned.length, done: done.length, overdue: overdue.length, open: mine.filter((t) => t.status !== 'done').length, hoursMs: ms, next: next ? { id: next.id, title: next.title, project: pname(next.projectId) } : null };
  }).sort((a, b) => (b.hoursMs > 0) - (a.hoursMs > 0) || b.planned - a.planned || b.hoursMs - a.hoursMs || String(a.member.name).localeCompare(String(b.member.name)));

  // Jobs this week: hours by job (everyone), who worked it, labour vs quote.
  const week = weekDays(weekKey(date));
  const wFrom = new Date(`${week[0]}T00:00:00`).getTime();
  const wTo = new Date(`${week[6]}T00:00:00`).getTime() + 86400000;
  const byJob = new Map();
  for (const s of sessions) {
    if (s.type === 'travel' || !s.projectId) continue;
    const ms = clipped(s, wFrom, wTo, now);
    if (!ms) continue;
    const j = byJob.get(s.projectId) || { projectId: s.projectId, ms: 0, people: new Set(), live: false };
    j.ms += ms;
    if (s.ownerUid) j.people.add(s.ownerUid);
    if (!s.endedAt && now - s.startedAt <= STALE_MS) j.live = true;
    byJob.set(s.projectId, j);
  }
  const jobFor = (pid) => { const p = project(pid); return p?.bookProjectId ? jobs.find((j) => j.id === p.bookProjectId || j.projectId === p.bookProjectId) : null; };
  const jobRows = [...byJob.values()].map((j) => ({
    projectId: j.projectId, name: pname(j.projectId) || 'Project', hoursMs: j.ms, people: [...j.people], live: j.live,
    openTasks: tasks.filter((t) => t.projectId === j.projectId && t.status !== 'done' && !t.bucket).length,
    labour: jobLabour(jobFor(j.projectId)),
  })).sort((a, b) => b.hoursMs - a.hoursMs);

  // Assistants (Willow): queue, working on, waiting for your review.
  const assistants = people.filter((m) => m.assistantFor).map((m) => {
    const mine = tasks.filter((t) => t.assignee === m.uid && !t.bucket);
    return {
      uid: m.uid, member: m, queued: mine.filter((t) => t.status !== 'done' && !inReview(t)).length,
      review: mine.filter(inReview).map((t) => ({ id: t.id, title: t.title, note: t.review?.note || null, at: t.review?.at || null, project: pname(t.projectId) })),
      doneThisWeek: mine.filter((t) => t.status === 'done' && t.completedAt >= wFrom && t.completedAt < wTo).length,
    };
  });

  // Needs you — the exceptions, most urgent first.
  const needs = [];
  for (const r of nowRows) if (r.state === 'stale') needs.push({ kind: 'clock-left-on', level: 'high', uid: r.uid, text: `${r.member.name}'s clock has been on since ${new Date(r.since).toLocaleString('en-CA', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`, at: r.since });
  const pendingH = Number(teamStats?.pending?.hours) || 0;
  if (pendingH > 0) needs.push({ kind: 'time-to-approve', level: 'medium', hours: pendingH, entries: teamStats.pending.entries, byWorker: teamStats.pending.byWorker || {}, text: `${pendingH.toFixed(pendingH % 1 ? 1 : 0)} h of time waiting for approval`, at: teamStats.pending.oldestAt || null });
  const reviewCount = assistants.reduce((a, x) => a + x.review.length, 0) + tasks.filter((t) => inReview(t) && !assistants.some((x) => x.uid === t.assignee)).length;
  if (reviewCount) needs.push({ kind: 'review', level: 'medium', count: reviewCount, text: `${reviewCount} task${reviewCount === 1 ? '' : 's'} handed back — waiting for your review` });
  for (const j of jobRows) if (j.labour?.over) needs.push({ kind: 'over-budget', level: 'high', projectId: j.projectId, text: `${j.name} is over its labour budget (${Math.round(j.labour.pct * 100)}%)` });
  for (const j of jobRows) if (j.labour && !j.labour.over && j.labour.pct !== null && j.labour.pct >= 0.85) needs.push({ kind: 'near-budget', level: 'low', projectId: j.projectId, text: `${j.name} has used ${Math.round(j.labour.pct * 100)}% of its labour budget` });
  for (const r of todayRows) if (r.overdue) needs.push({ kind: 'overdue', level: 'low', uid: r.uid, count: r.overdue, text: `${r.member.name} has ${r.overdue} overdue task${r.overdue === 1 ? '' : 's'}` });
  for (const r of todayRows) if (!r.open && r.uid !== me && r.member.role !== 'admin') needs.push({ kind: 'nothing-assigned', level: 'low', uid: r.uid, text: `${r.member.name} has nothing assigned` });
  if (hour >= 9) {
    const staffed = new Set(nowRows.filter((r) => r.state === 'on' || r.state === 'break' || r.state === 'driving').map((r) => r.projectId).filter(Boolean));
    const dueToday = new Set(tasks.filter((t) => t.scheduledDate === date && t.status !== 'done' && t.projectId && t.assignee && t.assignee !== me && people.some((m) => m.uid === t.assignee && !m.assistantFor)).map((t) => t.projectId));
    for (const pid of dueToday) if (!staffed.has(pid)) needs.push({ kind: 'job-unstaffed', level: 'medium', projectId: pid, text: `Nobody's on ${pname(pid) || 'a job'} — it has work assigned for today` });
  }
  // People P5: work left unassigned when someone's employment ended, and their final pay / ROE in the books (30 days).
  for (const l of leftoverTasks(tasks, members)) needs.push({ kind: 'left-unassigned', level: 'medium', uid: l.uid, count: l.tasks.length, taskIds: l.tasks.map((t) => t.id), text: `${l.tasks.length} task${l.tasks.length === 1 ? '' : 's'} left unassigned when ${l.name} left — give ${l.tasks.length === 1 ? 'it' : 'them'} to someone` });
  for (const m of pastMembers(members)) if (m.endedAt && now - m.endedAt < 30 * 86400000) needs.push({ kind: 'final-pay', level: 'low', uid: m.uid || m.id, text: `${m.name}: final pay and ROE in BattleBooks` });
  const rank = { high: 0, medium: 1, low: 2 };
  needs.sort((a, b) => rank[a.level] - rank[b.level]);

  // Feed: what the team did today (and if quiet, the last of this week), newest first.
  const feed = crew.flatMap((m) => memberActivity({ sessions, tasks, comments, notes }, m.uid, { from: wFrom, to: now + 1 }).map((a) => ({
    ...a, uid: m.uid, name: m.name, project: pname(a.projectId),
    photos: a.kind === 'done' ? ((tasks.find((t) => t.id === a.taskId)?.shots || []).map((x) => x.uri).filter((u) => /^https?:/.test(u || '')).slice(0, 3)) : [],
  }))).sort((a, b) => b.at - a.at).slice(0, 60);

  return {
    needs, now: nowRows, today: todayRows, jobs: jobRows, assistants, feed,
    past: pastMembers(members).map((m) => ({ ...m, uid: m.uid || m.id })),
    week: {
      days: week, crew: crew.length,
      hoursMs: crew.reduce((a, m) => a + memberDays(sessions, m.uid, date, now).totalMs, 0),
      onClock: nowRows.filter((r) => r.state === 'on' || r.state === 'break' || r.state === 'driving').length,
      doneThisWeek: tasks.filter((t) => t.status === 'done' && t.completedAt >= wFrom && t.completedAt < wTo && crew.some((m) => doneBy(t, m.uid))).length,
      labourUsed: jobRows.reduce((a, j) => a + (j.labour?.used || 0), 0),
    },
  };
}
